import {
  DATA_TREE_KEYWORDS,
  JAVASCRIPT_GLOBALS,
  JAVASCRIPT_KEYWORDS,
  PYTHON_GLOBALS,
  PYTHON_KEYWORDS,
} from "legacy/constants/WidgetValidation";
import { getNormalizedName } from "pages/Editors/ApiEditor/utils";
import { GLOBAL_FUNCTIONS } from "../../autocomplete/dataTreeTypeDefCreator";

export const removeSpecialChars = (
  value: string,
  options?: {
    limit?: number;
    allowSpaces?: boolean;
  },
) => {
  return getNormalizedName(value, options?.allowSpaces).slice(
    0,
    options?.limit || 30,
  );
};

// Function to convert CamelCase to dash-case
export const toDashedId = (str: string) =>
  str.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();

const NAME_REGEX = /^[a-zA-Z_][0-9a-zA-Z_]*$/;

/**
 * checks if the name is conflicting with
 * 1. API names,
 * 2. Queries name
 * 3. JavaScript reserved names
 * 4. Few internal function names that are in the evaluation tree
 *
 * return false if name does not conflicts with anything from the above list
 *
 * @param name
 * @param invalidNames
 */
export const validateName = (
  name: string | undefined,
  invalidNames: string[],
  skipKeywordCheck?: boolean,
  checkCaseInsensitive = false,
) => {
  if (!name || !name.length) {
    return "Name can't be empty";
  }

  if (!NAME_REGEX.test(name)) {
    return "Name should only contain letters, numbers and underscores and shouldn't start with a number";
  }

  if (invalidNames.includes(name)) {
    return "Name is already taken";
  }

  // check case insensitively
  if (
    checkCaseInsensitive &&
    invalidNames
      .map((value) => value.toLowerCase())
      .includes(name.toLowerCase())
  ) {
    return "Name is already taken. Name must be unique regardless of case.";
  }

  if (skipKeywordCheck) {
    return false;
  }

  if (JAVASCRIPT_KEYWORDS.includes(name)) {
    return "Name can't be a JavaScript keyword";
  }

  if (JAVASCRIPT_GLOBALS.includes(name)) {
    return "Name can't be a JavaScript global name";
  }

  if (PYTHON_KEYWORDS.includes(name)) {
    return "Name can't be a Python keyword";
  }

  if (PYTHON_GLOBALS.includes(name)) {
    return "Name can't be a Python global name";
  }

  if (DATA_TREE_KEYWORDS.includes(name)) {
    return "Name can't be a Superblocks data tree keyword";
  }

  if (name in GLOBAL_FUNCTIONS) {
    return "Name can't be a Superblocks global function";
  }

  return false;
};

/**
 * Type helper that more accurately narrows types when filtering for nullable values.
 */
export function isPresent<T>(t: T | undefined | null | void): t is T {
  return t !== undefined && t !== null;
}
